/**
 * §7.10 Enterprise Support — 3 support tiers matched to plans.
 */

import React from 'react'
import clsx from 'clsx'
import { Check, LifeBuoy, Sparkles, TrendingUp } from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { SUPPORT_TIERS } from '../data'

export default function Support() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Enterprise Support</Eyebrow>
          <SectionHeading align="center"
            title={<>Support that scales{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">with your business.</span></>}
            subtitle="Every tier includes real humans. Higher tiers add priority lanes, business consultations, and dedicated success — the highest-touch model reserved for Enterprise."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {SUPPORT_TIERS.map((t) => (
            <div key={t.id}
              className={clsx(
                'relative rounded-3xl border p-6 backdrop-blur-xl overflow-hidden flex flex-col gap-4 transition-all',
                t.highlight
                  ? 'border-brand-gold/40 bg-gradient-to-b from-brand-gold/[0.08] to-white/[0.01] shadow-[0_20px_70px_-20px_rgba(255,193,7,0.35)]'
                  : t.accent === 'green'
                    ? 'border-emerald-500/30 bg-gradient-to-b from-emerald-500/[0.05] to-white/[0.01]'
                    : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.03]',
              )}>
              {t.highlight && (
                <div className="pointer-events-none absolute inset-0 opacity-70"
                  style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.12), transparent 70%)' }} />
              )}

              <div className="relative flex items-center gap-3">
                <span className={clsx('inline-flex h-10 w-10 items-center justify-center rounded-xl border',
                  t.accent === 'orange' && 'border-brand-gold/35 bg-brand-gold/[0.08] text-brand-gold',
                  t.accent === 'green'  && 'border-emerald-500/35 bg-emerald-500/[0.08] text-emerald-400',
                )}>
                  {t.id === 'ent' ? <TrendingUp className="h-4 w-4" /> : <LifeBuoy className="h-4 w-4" />}
                </span>
                <div>
                  <div className="text-[13px] font-semibold text-white uppercase tracking-wider">{t.label}</div>
                  <div className="text-[10.5px] font-mono text-zinc-500">{t.matches}</div>
                </div>
              </div>

              <div className="relative text-[14.5px] font-semibold text-white leading-tight">{t.tagline}</div>

              <ul className="relative flex flex-col gap-1.5 mt-auto">
                {t.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-[13px] text-zinc-200">
                    <Check className={clsx('h-3.5 w-3.5 mt-0.5 shrink-0',
                      t.accent === 'green' ? 'text-emerald-400' : 'text-brand-gold')} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl px-5 md:px-6 py-4 flex items-start gap-3">
          <Sparkles className="h-4 w-4 text-brand-gold shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-300 leading-relaxed">
            <span className="font-semibold text-white">Every tier includes access to the full platform, all modules and continuous updates.</span>{' '}
            The distinction is speed, priority and named coverage.
          </p>
        </div>
      </Container>
    </section>
  )
}
